import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedNavBar from '../../components/ui/AuthenticatedNavBar';
import ProfileHeader from './components/ProfileHeader';
import ProfileEditForm from './components/ProfileEditForm';
import ActivitySummary from './components/ActivitySummary';
import RolePermissions from './components/RolePermissions';
import AccountSecurity from './components/AccountSecurity';
import SubscriptionManagement from './components/SubscriptionManagement';

const UserProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [activities, setActivities] = useState([]);
  const API_URL = import.meta.env?.VITE_SERVER_URL || 'http://localhost:4000';

  // Default UI-friendly user template
  const mockUser = {
    id: 'user_001',
    name: 'Journalist',
    email: 'user@example.com',
    role: 'Reporter',
    joinDate: '—',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
    stats: {
      articlesCreated: 0,
      verificationsRun: 0,
      biasChecks: 0
    },
    twoFactorEnabled: false,
    loginNotifications: true,
    lastLogin: '',
    pro: false,
    planId: null,
    billingCycle: null,
    usage: {
      tier: 'FREE_TIER',
      isProUser: false,
      newsGenerations: {
        used: 2,
        limit: 5,
        remaining: 3
      },
      verifications: {
        used: 7,
        limit: 10,
        remaining: 3
      },
      biasDetections: {
        used: 4,
        limit: 10,
        remaining: 6
      }
    }
  };

  // Mock activity data
  const mockActivities = [
    {
      type: 'article_created',
      title: 'Created new article',
      description: 'Climate Change Impact on Agriculture - Draft completed',
      timestamp: '2025-01-16 07:45:00',
      status: 'completed'
    },
    {
      type: 'fact_check',
      title: 'Fact-checked content',
      description: 'Verified claims about renewable energy statistics',
      timestamp: '2025-01-16 06:30:00',
      status: 'completed'
    },
    {
      type: 'bias_detection',
      title: 'Bias analysis completed',
      description: 'Analyzed political coverage for objectivity',
      timestamp: '2025-01-15 16:20:00',
      status: 'completed'
    },
    {
      type: 'profile_updated',
      title: 'Profile updated',
      description: 'Changed email preferences and notification settings',
      timestamp: '2025-01-15 14:15:00',
      status: 'completed'
    },
    {
      type: 'login',
      title: 'Account accessed',
      description: 'Logged in from Chrome on Windows',
      timestamp: '2025-01-15 09:00:00',
      status: 'completed'
    }
  ];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Prefer localStorage user loaded at login/register
        const stored = localStorage.getItem('user');
        const baseUser = stored ? JSON.parse(stored) : {};

        // Try fetching fresh user data from server (cookie-auth)
        const res = await fetch(`${API_URL}/api/user/data`, { credentials: 'include' });
        const data = await res.json();

        console.log('Server response:', data);

        // Extract ALL user data including subscription and usage info
        const serverUser = data?.success ? {
          name: data?.userData?.name,
          email: data?.userData?.email,
          isVerified: data?.userData?.isAccountVerified ?? false,
          pro: data?.userData?.pro ?? false,
          planId: data?.userData?.planId ?? null,
          billingCycle: data?.userData?.billingCycle ?? null,
          subscriptionStartDate: data?.userData?.subscriptionStartDate ?? null,
          subscriptionEndDate: data?.userData?.subscriptionEndDate ?? null,
          subscriptionCancelled: data?.userData?.subscriptionCancelled ?? false,
          usage: data?.userData?.usage ?? null
        } : {};

        // Merge into UI template - server data overrides everything
        const merged = {
          ...mockUser,
          ...baseUser,
          ...serverUser
        };

        console.log('Final merged user:', merged);
        console.log('Pro status:', merged.pro);

        setUser(merged);
        
        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(merged));
        
        setActivities(mockActivities);
      } catch (e) {
        console.error('Error loading user:', e);
        setUser(mockUser);
        setActivities(mockActivities);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API_URL]);

  const handleProfileUpdate = async (updateData) => {
    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update user data
      setUser(prev => ({
        ...prev,
        ...updateData
      }));

      // Add activity record
      const newActivity = {
        type: 'profile_updated',
        title: 'Profile updated',
        description: 'Successfully updated profile information',
        timestamp: new Date()?.toISOString(),
        status: 'completed'
      };

      setActivities(prev => [newActivity, ...prev?.slice(0, 4)]);

      // Show success message (in real app, use toast notification)
      alert('Profile updated successfully!');
      
    } catch (error) {
      console.error('Profile update failed:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSecurityUpdate = async (setting, value) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUser(prev => ({
        ...prev,
        [setting]: value
      }));

      alert(`Security setting updated successfully!`);
    } catch (error) {
      console.error('Security update failed:', error);
      alert('Failed to update security setting. Please try again.');
    }
  };

  const handleSubscriptionUpdate = (updatedUser) => {
    setUser(updatedUser);
    // Update localStorage with fresh subscription data
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      // ignore
    } finally {
      localStorage.removeItem('ddf_token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <AuthenticatedNavBar user={null} onLogout={handleLogout} />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-primary rounded-xl animate-pulse mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedNavBar user={user} onLogout={handleLogout} />
      <main className="pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-foreground">
              User Profile
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage your account settings, view activity, and update preferences
            </p>
          </div>

          {/* Profile Header */}
          <ProfileHeader user={user} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <ProfileEditForm 
                user={user} 
                onUpdate={handleProfileUpdate} 
                loading={loading} 
              />
              <RolePermissions role={user?.role} />
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <SubscriptionManagement 
                user={user} 
                onSubscriptionUpdate={handleSubscriptionUpdate} 
              />
              <ActivitySummary activities={activities} />
              <AccountSecurity 
                user={user} 
                onSecurityUpdate={handleSecurityUpdate} 
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfile;